// RFC 6238 TOTP (over RFC 4226 HOTP) via WebCrypto. Pure: the current time is
// always passed in by the caller, never read here — so this module is fully
// deterministic and testable against the RFC vectors. No dependencies, no
// network. Never logs the secret or the generated code.

const ALGOS = { SHA1: 'SHA-1', SHA256: 'SHA-256', SHA512: 'SHA-512' };
const B32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export function base32Decode(input) {
  if (typeof input !== 'string') return null;
  const s = input.toUpperCase().replace(/\s+/g, '').replace(/=+$/, '');
  if (s.length === 0) return null;
  let bits = 0;
  let value = 0;
  const out = [];
  for (const ch of s) {
    const idx = B32_ALPHABET.indexOf(ch);
    if (idx === -1) return null;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out.push((value >>> bits) & 0xff);
    }
  }
  return new Uint8Array(out);
}

export function parseOtpInput(input) {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;
  if (/^otpauth:\/\//i.test(trimmed)) return parseOtpauthUri(trimmed);
  const secret = base32Decode(trimmed);
  if (!secret || secret.length === 0) return null;
  return { secret, digits: 6, period: 30, algorithm: 'SHA1' };
}

function parseOtpauthUri(uri) {
  let u;
  try { u = new URL(uri); } catch { return null; }
  if (u.protocol.toLowerCase() !== 'otpauth:') return null;
  if (u.host.toLowerCase() !== 'totp') return null;

  const p = u.searchParams;
  const secret = base32Decode(p.get('secret') || '');
  if (!secret || secret.length === 0) return null;

  let digits = 6;
  if (p.has('digits')) {
    digits = toInt(p.get('digits'));
    if (digits === null || digits < 6 || digits > 8) return null;
  }

  let period = 30;
  if (p.has('period')) {
    period = toInt(p.get('period'));
    if (period === null || period <= 0) return null;
  }

  const algorithm = (p.get('algorithm') || 'SHA1').toUpperCase();
  if (!ALGOS[algorithm]) return null;

  const issuer = p.get('issuer') || undefined;
  let label;
  try { label = decodeURIComponent(u.pathname.replace(/^\//, '')) || undefined; } catch { label = undefined; }

  return { secret, digits, period, algorithm, issuer, label };
}

function toInt(v) {
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
}

export async function generateTotp({ secret, digits = 6, period = 30, algorithm = 'SHA1' }, epochSeconds) {
  const hash = ALGOS[algorithm] || 'SHA-1';
  let counter = Math.floor(epochSeconds / period);

  // 8-byte big-endian counter (division-based to handle values > 2^32).
  const msg = new Uint8Array(8);
  for (let i = 7; i >= 0; i--) {
    msg[i] = counter & 0xff;
    counter = Math.floor(counter / 256);
  }

  const key = await crypto.subtle.importKey('raw', secret, { name: 'HMAC', hash: { name: hash } }, false, ['sign']);
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', key, msg));

  const offset = sig[sig.length - 1] & 0x0f;
  const bin = ((sig[offset] & 0x7f) << 24) |
              ((sig[offset + 1] & 0xff) << 16) |
              ((sig[offset + 2] & 0xff) << 8) |
              (sig[offset + 3] & 0xff);

  return (bin % 10 ** digits).toString().padStart(digits, '0');
}
